import { useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useDropzone } from 'react-dropzone'
import type {
  PageData,
  ContinentSelection,
  CountrySelection,
} from '../../../../types'
import { usePostPortInfoFile } from '~hooks/queries/port-info/use-post-port-info-file'
import { useDeletePortInfoFile } from '~hooks/queries/port-info/use-delete-port-info-file'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { useDateFormat } from '~hooks/use-date-format'
import { RichTextEditor } from '~components/forms/rich-text-editor/rich-text-editor'
import { CalendarIcon, ClipIcon, CloseIcon } from '~components/icons'
import { COLORS } from '~theme/colors'
import {
  sectionBoxSx,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  fileItemContainerSx,
  fileLinkSx,
  fileDeleteButtonSx,
} from '../../../../styles'
import { getFilesForField } from '../../../../utils/form-utils'
import {
  ContinentAutocomplete,
  CountryAutocomplete,
} from './location-autocomplete'
import { VesselAutocomplete } from '../page-one/vessel-autocomplete'
import { inputWidth } from '../../add-visit-flow'
import { pools } from '~types/vessel.types'
import { useFormHandlers } from '../../../../hooks/use-form-handlers'

const REMARKS_FIELD_KEY = 'simpleRemarks'

export function PageTwoSimple({
  formData = {},
  updateFormData,
  portInfoId,
  files = [],
  missingFields = [],
}: PageData) {
  const { setStatus } = useAnimatedAlert()
  const { mutate: uploadFile, isPending: isUploading } = usePostPortInfoFile()
  const { mutate: deleteFile } = useDeletePortInfoFile()
  const dateFormat = useDateFormat('literal')
  const { handleVesselChange, onChangeHandler } = useFormHandlers(
    formData,
    updateFormData,
  )

  const isContinent = formData.type === 'continent'

  const attachedFiles = getFilesForField(files, REMARKS_FIELD_KEY)

  function handleContinentChange(value: ContinentSelection | undefined) {
    updateFormData({ ...formData, continent: value })
  }

  function handleCountryChange(value: CountrySelection | undefined) {
    updateFormData({ ...formData, country: value })
  }

  function handleDateOfInformationChange(date: Date | null) {
    updateFormData({ ...formData, dateOfInformation: date || undefined })
  }

  function handleSubjectChange(e: React.ChangeEvent<HTMLInputElement>) {
    updateFormData({ ...formData, subject: e.target.value })
  }

  function handleRemarksChange(value: string) {
    updateFormData({ ...formData, remarks: value })
  }

  function handleRemoveFile(fileId: string) {
    if (!portInfoId) return
    deleteFile(
      { fileId, portInfoId },
      {
        onSuccess: () => {
          setStatus('File deleted', undefined, 'success')
        },
        onError: () => {
          setStatus('Failed to delete file', undefined, 'error')
        },
      },
    )
  }

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: { file: File }[]) => {
      if (!portInfoId) {
        setStatus('Cannot upload file: entry not saved yet', undefined, 'error')
        return
      }

      if (fileRejections.length > 0) {
        const names = fileRejections.map((r) => r.file.name).join(', ')
        setStatus(
          `File(s) too large (max ${MAX_FILE_SIZE_LABEL}): ${names}`,
          undefined,
          'error',
        )
      }

      if (acceptedFiles.length > 0) {
        let successCount = 0
        let errorCount = 0
        const totalFiles = acceptedFiles.length

        acceptedFiles.forEach((file) => {
          uploadFile(
            {
              portInfoId,
              field: REMARKS_FIELD_KEY,
              file,
              title: file.name,
            },
            {
              onSuccess: () => {
                successCount++
                if (successCount + errorCount === totalFiles) {
                  if (errorCount === 0) {
                    setStatus(
                      totalFiles === 1
                        ? 'File uploaded successfully'
                        : `${successCount} files uploaded successfully`,
                      undefined,
                      'success',
                    )
                  } else {
                    setStatus(
                      `${successCount} uploaded, ${errorCount} failed`,
                      undefined,
                      'error',
                    )
                  }
                }
              },
              onError: () => {
                errorCount++
                if (successCount + errorCount === totalFiles) {
                  setStatus(
                    `${successCount} uploaded, ${errorCount} failed`,
                    undefined,
                    'error',
                  )
                }
              },
            },
          )
        })
      }
    },
    [portInfoId, uploadFile, setStatus],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: true,
  })

  return (
    <Box sx={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto', pr: 1 }}>
      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Details</Typography>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {isContinent ? (
            <ContinentAutocomplete
              value={formData.continent}
              onOptionChange={handleContinentChange}
              error={missingFields.includes('continent')}
            />
          ) : (
            <CountryAutocomplete
              value={formData.country}
              onOptionChange={handleCountryChange}
              error={missingFields.includes('country')}
            />
          )}

          <VesselAutocomplete
            vesselName={formData?.vesselName}
            imoNumber={formData?.imoNumber}
            onVesselChange={handleVesselChange}
            error={
              missingFields.includes('vesselName') ||
              missingFields.includes('imoNumber')
            }
          />

          <FormControl size="small" sx={{ width: inputWidth }} required>
            <InputLabel id="pool-select-label-simple">Pool/Segment</InputLabel>
            <Select
              labelId="pool-select-label-simple"
              label="Pool/Segment"
              value={formData?.pool || ''}
              onChange={(e) =>
                onChangeHandler({
                  key: 'pool',
                  value: e.target.value || undefined,
                })
              }
              MenuProps={{ disablePortal: true }}
            >
              {pools.map((pool) => (
                <MenuItem key={pool.value} value={pool.value}>
                  {pool.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            format={dateFormat}
            slotProps={{
              textField: {
                size: 'small',
                error: missingFields.includes('dateOfInformation'),
              },
              popper: {
                disablePortal: true,
              },
            }}
            sx={{ width: inputWidth }}
            value={formData?.dateOfInformation || null}
            label="Date of Information *"
            onChange={handleDateOfInformationChange}
          />
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Subject</Typography>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            size="small"
            sx={{ width: { xs: '100%', md: '692px' } }}
            value={formData?.subject || ''}
            onChange={handleSubjectChange}
            placeholder="Enter a descriptive subject for this entry *"
            error={missingFields.includes('subject')}
          />
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="paragraphBold2">Remarks</Typography>
          <Typography
            variant="caption"
            sx={{
              color: COLORS.greys.dark,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ClipIcon style={{ fontSize: '0.875rem' }} />
            Drag & drop files (max {MAX_FILE_SIZE_LABEL} per file), or click
            "Attach Files"
          </Typography>
        </Box>

        <Box
          {...getRootProps()}
          sx={{
            mt: 3,
            border: '1px solid',
            borderColor: isDragActive
              ? COLORS.secondary.midBlue
              : COLORS.greys.light,
            borderStyle: isDragActive ? 'dashed' : 'solid',
            borderRadius: 1,
            p: 2,
            backgroundColor: isDragActive
              ? COLORS.secondary.blueTint
              : undefined,
            transition: 'border-color 0.2s, background-color 0.2s',
            position: 'relative',
          }}
        >
          {isDragActive && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: COLORS.greys.almostWhite,
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="paragraph2"
                sx={{ color: COLORS.secondary.midBlue }}
              >
                Drop files here to attach (max {MAX_FILE_SIZE_LABEL} per file)
              </Typography>
            </Box>
          )}
          <input {...getInputProps()} />
          <RichTextEditor
            value={formData.remarks || ''}
            onChange={handleRemarksChange}
            placeholder="Enter remarks..."
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mt: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {attachedFiles.map((file) => (
                <Box key={file.id} sx={fileItemContainerSx}>
                  <Box
                    component="a"
                    href={file.signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`${file.name} (${file.fileSize})`}
                    sx={{ ...fileLinkSx, flex: 1, minWidth: 0 }}
                  >
                    {file.name} ({file.fileSize})
                  </Box>
                  <Tooltip title="Delete file">
                    <IconButton
                      size="small"
                      className="file-delete-button"
                      sx={fileDeleteButtonSx}
                      onClick={() => handleRemoveFile(file.id)}
                    >
                      <CloseIcon style={{ fontSize: '0.875rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {isUploading && (
                <Typography variant="caption" color="text.secondary">
                  Uploading...
                </Typography>
              )}
              <Tooltip title="Attach files">
                <IconButton
                  onClick={open}
                  disabled={isUploading || !portInfoId}
                >
                  <ClipIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
